/// <amd-module name="Search/Wiki/Flux/StoresHub" />
import * as CompositeStoresManager from "Search/Shared/Base/Stores/CompositeStoresManager";
import { IStoresHub } from "Search/Shared/Base/Container";
import { Action } from "VSS/Reflux/Action";
import { ActionsHub } from "Search/Wiki/Flux/ActionsHub";
import { Filter } from "SearchUI/Utilities/Filter";
import { FilterStore } from "Search/Wiki/Flux/Stores/FilterStore";
import { IFilterStoreState } from "Search/Shared/Base/Stores/FilterStore";
import { IProjectData } from "Tfs/Platform/Page"
import { ISearchStoreState } from "Search/Shared/Base/Stores/SearchStore2";
import { IVssPageContext } from "VSS/Platform/Context";
import { OrganizationInfoStore, IOrganizationInfoState } from "Search/Shared/Base/Stores/OrganizationInfoStore";
import { SearchStore } from "Search/Wiki/Flux/Stores/SearchStore";
import { WikiSearchRequest, WikiSearchResponse } from "Search/WebApi/Contracts/Search.Shared";
import { ZeroDataState } from "Search/Shared/Base/Stores/ZeroDataStore2";
import { ZeroDataStore } from "Search/Wiki/Flux/Stores/ZeroDataStore";

export type StoreName =
    "searchStore" |
    "filterStore" |
    "zeroDataStore" |
    "organizationInfoStore";

export interface IAggregatedState {
    searchStoreState: ISearchStoreState<WikiSearchRequest, WikiSearchResponse>;
    filterStoreState: IFilterStoreState;
    zeroDataState: ZeroDataState;
    organizationInfoState: IOrganizationInfoState;
    isProjectContext: boolean;
}

export class StoresHub implements IStoresHub<StoreName, IAggregatedState> {
    public searchStore: SearchStore;
    public filterStore: FilterStore;
    public zeroDataStore: ZeroDataStore;
    public organizationInfoStore: OrganizationInfoStore;

    private readonly compositeStoresManager = new CompositeStoresManager.CompositeStoresManager();
    private readonly listener: CompositeStoresManager.ListeningActionsManager;

    constructor(
        actionsHub: ActionsHub,
        private readonly pageContext: IVssPageContext,
        filter: Filter,
        private readonly project: IProjectData | undefined,
        private readonly onDispatched?: CompositeStoresManager.EmitChangedFunction<any>) {
        this.listener = new CompositeStoresManager.ListeningActionsManager(this.emitChanged);
        this.searchStore = this.createSearchStore(actionsHub);
        this.zeroDataStore = this.createZeroDataStore(actionsHub);
        this.organizationInfoStore = this.createOrganizationInfoStore(actionsHub);
        this.filterStore = this.createFilterStore(actionsHub, this.pageContext, filter);
    }

    public getAggregatedState = (): IAggregatedState => {
        return {
            searchStoreState: this.searchStore.state,
            filterStoreState: this.filterStore.state,
            zeroDataState: this.zeroDataStore.state,
            organizationInfoState: this.organizationInfoStore.state,
            isProjectContext: !!this.project
        };
    }

    public getCompositeStore(storeNames: StoreName[]): CompositeStoresManager.CompositeStore {
        return this.compositeStoresManager.getOrCreate(storeNames);
    }

    public dispose = (): void => {
        this.listener.dispose();
        this.compositeStoresManager.dispose();
    }

    private createZeroDataStore(actionsHub: ActionsHub): ZeroDataStore {
        const store = new ZeroDataStore("ZeroDataStoreChanged");

        this.listener.listen(actionsHub.pageInitializationStarted, "zeroDataStore", (payload) => {
            if (payload.isLandingPage) {
                store.onLandingPage();
            }
        });
        this.listener.listen(actionsHub.resultsLoaded, "zeroDataStore", (payload) => {
            const { searchStoreState } = this.getAggregatedState();
            store.onResultsLoaded(searchStoreState.query, payload);
        });
        this.listener.listen(actionsHub.searchFailed, "zeroDataStore", (payload) => {
            const { searchStoreState } = this.getAggregatedState();
            store.onSearchFailed(payload, searchStoreState.query);
        });
        this.listener.listen(actionsHub.searchStarted, "zeroDataStore", store.reset);

        return store;
    }

    private createSearchStore(actionsHub: ActionsHub): SearchStore {
        const store = new SearchStore("SearchStoreChanged");
        this.listener.listen(actionsHub.pageInitializationStarted, "searchStore", (payload) => {
            // On landing page update the query state for an adequate search store state
            payload.isLandingPage ? store.updateQuery(payload) : store.startSearch(payload);
        });
        this.listener.listen(actionsHub.resultsLoaded, "searchStore", store.loadSearchResults);
        this.listener.listen(actionsHub.searchStarted, "searchStore", store.startSearch);
        this.listener.listen(actionsHub.searchFailed, "searchStore", store.failSearch);
        return store;
    }

    private createFilterStore(
        actionsHub: ActionsHub,
        pageContext: IVssPageContext,
        filter: Filter): FilterStore {
        const store = new FilterStore("FilterStoreChanged", pageContext, filter, this.project);

        this.listener.listen(actionsHub.resultsLoaded, "filterStore", (payload) => {
            const { searchStoreState } = this.getAggregatedState();
            const { query } = searchStoreState;
            store.updateFilters(query, payload);
        });

        this.listener.listen(actionsHub.filterPaneToggled, "filterStore", store.toggleFilterPane);

        return store;
    }

    private createOrganizationInfoStore(actionsHub: ActionsHub): OrganizationInfoStore {
        const store = new OrganizationInfoStore("OrgInfoStoreChanged");
        this.listener.listen(actionsHub.organizationInfoLoaded, "organizationInfoStore", store.onOrganizationInfoLoaded);
        this.listener.listen(actionsHub.organizationInfoLoadFailed, "organizationInfoStore", store.onOrganizationInfoLoadFailed);
        this.listener.listen(actionsHub.errorNotificationBannerDismissed, "organizationInfoStore", store.onResetOrganizationInfoLoadStatus);
        this.listener.listen(actionsHub.searchStarted, "organizationInfoStore", store.onResetOrganizationInfoLoadStatus);
        return store;
    }

    private emitChanged = (changedStores: string[], action: Action<any>, payload: any): void => {
        this.compositeStoresManager.emitCompositeChanged(changedStores);

        if (this.onDispatched) {
            this.onDispatched(changedStores, action, payload);
        }
    }
}
